import type { Question } from "./ai/content-schemas.js";

/**
 * Regras de aprendizagem compartilhadas: correção de questões, versão pública das questões
 * (sem gabarito), repetição espaçada (SM-2) e desbloqueio da trilha. Seguro para o navegador.
 */

// ─────────────────────────── Questões ───────────────────────────

export type PublicQuestion =
  | { id: string; kind: "multiple_choice"; prompt: string; options: Array<{ id: string; text: string }> }
  | { id: string; kind: "true_false"; prompt: string }
  | { id: string; kind: "matching"; prompt: string; left: string[]; right: string[] };

/** Embaralhamento determinístico (mesma ordem para o mesmo aluno e questão). */
export function seededShuffle<T>(items: T[], seed: string): T[] {
  let h = 2166136261;
  for (const c of seed) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0;
    const j = h % (i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Questão sem resposta nem explicações, para exibir ao aluno antes da correção. */
export function toPublicQuestion(id: string, q: Question, seed = id): PublicQuestion {
  if (q.kind === "multiple_choice")
    return {
      id,
      kind: q.kind,
      prompt: q.prompt,
      options: q.options.map((o) => ({ id: o.id, text: o.text })),
    };
  if (q.kind === "true_false") return { id, kind: q.kind, prompt: q.prompt };
  return {
    id,
    kind: q.kind,
    prompt: q.prompt,
    left: q.pairs.map((p) => p.left),
    right: seededShuffle(
      q.pairs.map((p) => p.right),
      seed,
    ),
  };
}

export type QuestionFeedback = {
  questionId: string;
  correct: boolean;
  explanation: string;
  /** Resposta correta: letra (múltipla escolha), booleano (V/F) ou correspondências na ordem dos itens (associação). */
  correctAnswer: string | boolean | string[];
  /** Explicação de cada alternativa (múltipla escolha). */
  optionExplanations?: Record<string, string>;
};

/** Corrige a resposta do aluno. Associação: lista das correspondências escolhidas, na ordem dos itens. */
export function gradeQuestion(id: string, q: Question, answer: unknown): QuestionFeedback {
  if (q.kind === "multiple_choice") {
    const chosen = q.options.find((o) => o.id === answer);
    const right = q.options.find((o) => o.id === q.correctOptionId)!;
    return {
      questionId: id,
      correct: answer === q.correctOptionId,
      explanation: chosen?.explanation ?? right.explanation,
      correctAnswer: q.correctOptionId,
      optionExplanations: Object.fromEntries(q.options.map((o) => [o.id, o.explanation])),
    };
  }
  if (q.kind === "true_false")
    return {
      questionId: id,
      correct: answer === q.answer,
      explanation: q.explanation,
      correctAnswer: q.answer,
    };
  const expected = q.pairs.map((p) => p.right);
  const given = Array.isArray(answer) ? answer.map(String) : [];
  return {
    questionId: id,
    correct: given.length === expected.length && expected.every((r, i) => given[i] === r),
    explanation: q.explanation,
    correctAnswer: expected,
  };
}

export const scorePercent = (feedback: QuestionFeedback[]) =>
  feedback.length ? Math.round((feedback.filter((f) => f.correct).length / feedback.length) * 100) : 0;

// ─────────────────────────── Repetição espaçada (SM-2) ───────────────────────────

export type ReviewState = { easiness: number; intervalDays: number; repetitions: number };

/** Notas da revisão de flashcards, na escala do SM-2. */
export const REVIEW_GRADES = [
  { grade: 1, label: "Errei" },
  { grade: 3, label: "Difícil" },
  { grade: 4, label: "Bom" },
  { grade: 5, label: "Fácil" },
] as const;

/**
 * Algoritmo SM-2 (SuperMemo 2): notas abaixo de 3 reiniciam a sequência; acima, o intervalo cresce
 * 1 → 6 dias → intervalo × facilidade. A facilidade nunca fica abaixo de 1,3.
 */
export function sm2(state: ReviewState, grade: number, now = new Date()): ReviewState & { dueAt: Date } {
  const q = Math.max(0, Math.min(5, Math.round(grade)));
  let { easiness, intervalDays, repetitions } = state;
  if (q < 3) {
    repetitions = 0;
    intervalDays = 0; // volta ainda hoje, ao fim da sessão
  } else {
    repetitions += 1;
    intervalDays = repetitions === 1 ? 1 : repetitions === 2 ? 6 : Math.round(intervalDays * easiness);
  }
  easiness = Math.max(1.3, easiness + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)));
  const dueAt = new Date(now.getTime() + (intervalDays === 0 ? 10 * 60_000 : intervalDays * 86_400_000));
  return { easiness: Math.round(easiness * 100) / 100, intervalDays, repetitions, dueAt };
}

// ─────────────────────────── Trilha ───────────────────────────

export type LessonState = "locked" | "available" | "in_progress" | "completed";

/** Situação de cada aula na ordem da trilha, considerando o desbloqueio sequencial. */
export function lessonStates(
  orderedIds: string[],
  progress: Map<string, { completed: boolean }>,
  sequential: boolean,
): Map<string, LessonState> {
  const out = new Map<string, LessonState>();
  let previousDone = true;
  for (const id of orderedIds) {
    const p = progress.get(id);
    const state: LessonState = p?.completed ? "completed" : p ? "in_progress" : "available";
    out.set(id, sequential && !previousDone && state !== "completed" ? "locked" : state);
    previousDone = state === "completed";
  }
  return out;
}

export const progressPercent = (completed: number, total: number) =>
  total ? Math.min(100, Math.round((completed / total) * 100)) : 0;

/** Código de verificação do certificado (sem caracteres ambíguos). */
export function certificateCode(random: () => number = Math.random): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const pick = () =>
    Array.from({ length: 4 }, () => alphabet[Math.floor(random() * alphabet.length)]).join("");
  return `B2C-${pick()}-${pick()}-${pick()}`;
}

// ─────────────────────────── Vídeos do professor (YouTube e Vimeo) ───────────────────────────

export type VideoEmbed = { provider: "youtube" | "vimeo"; id: string; embedUrl: string; watchUrl: string };

/**
 * Reconhece links do YouTube e do Vimeo e devolve o endereço de incorporação. Os vídeos ficam hospedados
 * nesses serviços: a plataforma guarda apenas o link. O YouTube é incorporado no modo de privacidade reforçada.
 */
export function parseVideoUrl(raw: string): VideoEmbed | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const host = u.hostname.replace(/^www\.|^m\./, "");
  const yt = (id: string | null | undefined): VideoEmbed | null =>
    id && /^[A-Za-z0-9_-]{11}$/.test(id)
      ? {
          provider: "youtube",
          id,
          embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0`,
          watchUrl: `https://www.youtube.com/watch?v=${id}`,
        }
      : null;
  if (host === "youtu.be") return yt(u.pathname.split("/")[1]);
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    if (u.pathname === "/watch") return yt(u.searchParams.get("v"));
    const m = /^\/(embed|shorts|live|v)\/([^/?#]+)/.exec(u.pathname);
    return m ? yt(m[2]) : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // eslint-disable-next-line security/detect-unsafe-regex -- repetições limitadas e ancoradas; aplicada ao caminho de uma URL
    const m = /^\/(?:video\/)?(\d{6,12})(?:\/([0-9a-f]{6,20}))?/.exec(u.pathname);
    if (!m) return null;
    const hash = m[2] ?? u.searchParams.get("h");
    return {
      provider: "vimeo",
      id: m[1]!,
      embedUrl: `https://player.vimeo.com/video/${m[1]}?dnt=1${hash ? `&h=${hash}` : ""}`,
      watchUrl: `https://vimeo.com/${m[1]}${hash ? `/${hash}` : ""}`,
    };
  }
  return null;
}

// ─────────────────────────── Relatos de problemas no conteúdo ───────────────────────────

export const REPORT_CATEGORIES = {
  incorrect: "Conteúdo incorreto",
  source_mismatch: "Divergência com o ebook",
  wrong_answer: "Gabarito errado",
  language: "Erro de português ou digitação",
  technical: "Problema técnico na plataforma",
  other: "Outro",
} as const;
export type ReportCategory = keyof typeof REPORT_CATEGORIES;

export const REPORT_ITEM_TYPES = {
  lesson: "Aula",
  question: "Questão",
  flashcard: "Flashcard",
  case_study: "Estudo de caso",
  exercise: "Exercício prático",
  glossary: "Glossário",
  platform: "Plataforma",
} as const;
export type ReportItemType = keyof typeof REPORT_ITEM_TYPES;

export const REPORT_STATUS = {
  open: "Aberto",
  reviewing: "Em análise",
  resolved: "Corrigido",
  dismissed: "Descartado",
} as const;
export type ReportStatus = keyof typeof REPORT_STATUS;
